import { Box, IconButton, Stack, Typography, Tooltip, Chip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import { ItemCategory, ItemType } from '../schema';
import { canEditCategory, canDeleteCategory, getCategoryItemCount } from '../utils/categoryUtils';

interface Props {
    category: ItemCategory;
    onEdit: (category: ItemCategory) => void;
    onDelete: (category: ItemCategory) => void;
    itemTypes: ItemType[];
}

export default function CategoryRow({ category, onEdit, onDelete, itemTypes }: Props) {
    const editable = canEditCategory(category);
    const itemCount = getCategoryItemCount(category.id, itemTypes || []);
    const deleteCheck = canDeleteCategory(category, itemTypes || []);

    const handleEdit = () => {
        if (editable) {
            onEdit(category);
        }
    };

    const handleDelete = () => {
        if (deleteCheck.canDelete) {
            onDelete(category);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                border: '1px solid rgba(0,0,0,0.1)',
                borderRadius: 1,
                backgroundColor: category.systemDefined ? 'rgba(0,0,0,0.02)' : 'white',
            }}
        >
            {category.color && (
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: category.color,
                        border: '1px solid rgba(0,0,0,0.1)',
                        flexShrink: 0,
                    }}
                />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body1" fontWeight={500}>
                        {category.name}
                    </Typography>
                    {category.systemDefined && (
                        <Tooltip title="System-defined category">
                            <LockIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </Tooltip>
                    )}
                </Stack>
                {category.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {category.description}
                    </Typography>
                )}
                {itemCount > 0 && (
                    <Chip
                        label={`${itemCount} item${itemCount !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                    />
                )}
            </Box>
            <Stack direction="row" spacing={1}>
                <Tooltip title={editable ? 'Edit category' : 'System categories cannot be edited'}>
                    <span>
                        <IconButton size="small" onClick={handleEdit} disabled={!editable}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={deleteCheck.canDelete ? 'Delete category' : deleteCheck.reason || 'Cannot delete'}>
                    <span>
                        <IconButton size="small" onClick={handleDelete} disabled={!deleteCheck.canDelete} color="error">
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        </Box>
    );
}
